import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { API_SCOPE, isAuthConfigured, msalInstance } from '../auth/msal.config';
import { config } from '../config';

const API_BASE = config.apiBaseUrl;

/**
 * Ansicht als andere Person.
 *
 * Im `sessionStorage` und nicht im `localStorage`: Die Ansicht endet mit dem
 * Tab. Ein Zustand, der ein neues Fenster überdauert, wäre die verlässlichste
 * Art, ihn zu vergessen — und dann wundert man sich Tage später über eine
 * Oberfläche, in der die Verwaltung fehlt.
 */
const IMPERSONATION_KEY = 'fleet.impersonate';
const IMPERSONATION_HEADER = 'X-Impersonate-User-Id';

export function impersonatedUserId(): string | null {
  return sessionStorage.getItem(IMPERSONATION_KEY);
}

export function setImpersonatedUserId(userId: string | null): void {
  if (userId) sessionStorage.setItem(IMPERSONATION_KEY, userId);
  else sessionStorage.removeItem(IMPERSONATION_KEY);
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Beschafft ein Access-Token für unsere API.
 *
 * MSAL hält Tokens intern vor und erneuert sie im Hintergrund; erst wenn das
 * nicht mehr geht — abgelaufene Sitzung, geänderte Bedingungen — ist eine
 * Interaktion nötig. Diese wird bewusst nur bei `InteractionRequiredAuthError`
 * ausgelöst und nicht bei jedem Fehler, sonst öffnet sich bei einer
 * Netzwerkstörung ungefragt ein Anmeldefenster.
 */
async function acquireToken(): Promise<string> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) throw new ApiError(401, 'NOT_SIGNED_IN', 'Nicht angemeldet.');

  try {
    const result = await msalInstance.acquireTokenSilent({ account, scopes: [API_SCOPE] });
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      const result = await msalInstance.acquireTokenPopup({ account, scopes: [API_SCOPE] });
      return result.accessToken;
    }
    throw error;
  }
}

/**
 * Ruft die API auf und übersetzt Fehlerantworten in `ApiError`.
 *
 * Der `code` ist dabei das, worauf die Oberfläche verzweigt — nie der Text.
 * Ein `BOOKING_SLOT_TAKEN` soll den Kalender neu laden lassen, ein
 * `NOT_ALLOWED_TO_BOOK_GROUP` dagegen eine Erklärung zeigen; an der Formulierung
 * ließe sich das nicht zuverlässig unterscheiden.
 */
export async function apiFetch<T>(
  path: string,
  init: RequestInit & {
    /**
     * Bewusst als die handelnde Person selbst aufrufen.
     *
     * Gebraucht für genau einen Fall: das Umschalten der Ansicht. Der Server
     * weist in fremder Ansicht jeden Schreibzugriff ab — auch den, mit dem man
     * die Ansicht wechseln will.
     */
    asSelf?: boolean;
  } = {},
): Promise<T> {
  const { asSelf, ...request } = init;
  const headers = new Headers(request.headers);
  headers.set('Content-Type', 'application/json');

  if (isAuthConfigured) {
    headers.set('Authorization', `Bearer ${await acquireToken()}`);
  }

  const impersonate = asSelf ? null : impersonatedUserId();
  if (impersonate) headers.set(IMPERSONATION_HEADER, impersonate);

  const response = await fetch(`${API_BASE}${path}`, { ...request, headers });

  if (!response.ok) {
    let code = `HTTP_${response.status}`;
    let message = `Die Anfrage ist fehlgeschlagen (${response.status}).`;
    let details: unknown;

    try {
      const body = (await response.json()) as { code?: string; message?: string; details?: unknown };
      if (body.code) code = body.code;
      if (body.message) message = body.message;
      details = body.details;
    } catch {
      // Antwort ohne JSON-Körper — die Vorgabewerte oben genügen.
    }

    throw new ApiError(response.status, code, message, details);
  }

  /**
   * Antwort ohne Körper.
   *
   * Nicht nur 204 prüfen: Ein Handler, der `void` zurückgibt, antwortet in
   * NestJS mit **200 und leerem Body**. `response.json()` scheitert daran mit
   * „Unexpected end of JSON input" — einer Meldung, die nach einem kaputten
   * Server klingt, obwohl der Aufruf erfolgreich war. Deshalb wird der Text
   * gelesen und nur geparst, wenn tatsächlich einer da ist.
   *
   * **Und daraus wird `null`, nicht `undefined`.** Das ist kein Geschmack,
   * sondern hart erlernt: Der Express-Adapter von NestJS prüft `isNil(body)` und
   * sendet damit auch für ein **ausdrücklich zurückgegebenes `null`** einen
   * leeren Körper — ein Endpunkt, der „es gibt hier nichts" meldet, ist von einem
   * `void`-Handler über HTTP nicht zu unterscheiden. React Query 5 wiederum
   * **verbietet** `undefined` als Abfrageergebnis und wirft dann
   * „data is undefined"; `null` nimmt es an. Ohne diese Zeile bräuchte jeder
   * Endpunkt mit „vielleicht nichts" ein eigenes `?? null` an der Aufrufstelle —
   * und der erste, der es vergisst, bekommt einen Fehler, der nicht nach seiner
   * Ursache aussieht.
   */
  const text = await response.text();
  if (text === '') return null as unknown as T;
  return JSON.parse(text) as T;
}

export interface MeResponse {
  kind: 'USER' | 'SERVICE' | 'API_KEY';
  userId?: string;
  displayName: string;
  userPrincipalName?: string;
  roles: string[];
  capabilities: string[];
  /**
   * Darf der Aufrufer irgendwo Buchungen freigeben?
   *
   * Nicht aus `capabilities` ableitbar: Die sind für jeden Menschen gleich,
   * das Freigaberecht hängt dagegen an den Fahrzeuggruppen.
   */
  canApproveBookings: boolean;
  groupCount?: number;
  /**
   * Gesetzt, wenn hier gerade jemand mit fremden Rechten schaut. Alle übrigen
   * Felder beschreiben dann die angesehene Person.
   */
  impersonatedBy?: { userId: string; displayName: string };
}

export const api = {
  me: () => apiFetch<MeResponse>('/api/v1/me'),
  health: () => apiFetch<{ status: string }>('/health/ready'),

  /**
   * Beginnt eine Ansicht mit fremden Rechten.
   *
   * `asSelf`, weil der Server in fremder Ansicht jeden Schreibzugriff abweist —
   * auch diesen. Der Aufruf schaltet nichts frei; er prüft die Zielperson und
   * schreibt den Vorgang ins Protokoll. Umgeschaltet wird über die Kopfzeile,
   * die bei jeder Anfrage neu geprüft wird.
   */
  startImpersonation: (userId: string) =>
    apiFetch<{ userId: string; displayName: string }>('/api/v1/impersonation', {
      method: 'POST',
      body: JSON.stringify({ userId }),
      asSelf: true,
    }),
};
